import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { GridLoader } from "react-spinners";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  buscarSumarizacaoContribuicoesApuracao,
  buscarLancamentosContribuicoesApuracao,
} from "../../../services/apuracao.service";
import Pagination from "../../../components/Pagination";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import BreadcrumbApuracao from "../components/BreadcrumbApuracao";
import SelectFiltro, { BuscaFiltro } from "../components/SelectFiltro";
import { formatarCnpj, fmtMesAno } from "../utils";
import { formatarMoedaBRL } from "../Detalhe/utils/calcularTotaisPorAno.util";
import styles from "../Detalhe/detalhe.styles.module.css";
import tableStyles from "../../../components/TableCard/TableView/styles.module.css";

const BLOCO_OPCOES = [
  { value: "", label: "Todos" },
  { value: "M200", label: "M200 — Consolidação PIS" },
  { value: "M210", label: "M210 — Detalhamento PIS" },
  { value: "M600", label: "M600 — Consolidação COFINS" },
  { value: "M610", label: "M610 — Detalhamento COFINS" },
  { value: "F100", label: "F100 — Demais Operações" },
  { value: "C170", label: "C170 — Itens de Documento Fiscal" },
];

function competenciaYm(competencia) {
  return String(competencia || "").slice(0, 7);
}

function exportarCsv(linhas, nomeArquivo) {
  const cabecalho = ["bloco_registro", "descricao", "natureza", "valor"].join(";");
  const corpo = linhas.map((l) =>
    [l.blocoRegistro, l.descricao, l.natureza, l.valor]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
      .join(";")
  );
  const blob = new Blob([[cabecalho, ...corpo].join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeArquivo;
  a.click();
  URL.revokeObjectURL(url);
}

export default function LancamentosContribuicoes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // TXM-600 (2026-09-10) — a aba Contribuições passou a agregar por bloco (sem coluna/filtro
  // Competência), então o botão "Detalhes" de cada linha já escolheu o bloco — chega fixo na
  // URL, não é mais um filtro interativo aqui (mesmo tratamento dado a "Conta contábil" em
  // LancamentosEcd). Competência continua existindo, mas como filtro OPCIONAL: "" = todo o
  // período da apuração.
  const bloco = searchParams.get("bloco") || "";
  const competenciaInicial = searchParams.get("competencia") || "";

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [apuracao, setApuracao] = useState(null);
  const [competencia, setCompetencia] = useState(competenciaInicial);
  const [competenciasDisponiveis, setCompetenciasDisponiveis] = useState([]);
  const [busca, setBusca] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [pagina, setPagina] = useState(1);
  const [total, setTotal] = useState(0);
  const [linhas, setLinhas] = useState([]);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    let ativo = true;
    async function carregarApuracaoEDominios() {
      const res = await buscarSumarizacaoContribuicoesApuracao(id);
      if (!ativo || !res.sucesso) return;
      setApuracao(res.apuracao || null);
      const ymSet = new Set(
        (res.linhas || []).map((l) => competenciaYm(l.competencia))
      );
      setCompetenciasDisponiveis(Array.from(ymSet).sort());
    }
    carregarApuracaoEDominios();
    return () => {
      ativo = false;
    };
  }, [id]);

  const carregarLancamentos = useCallback(async () => {
    setLoading(true);
    setErro("");
    const res = await buscarLancamentosContribuicoesApuracao(id, {
      competencia: competencia || undefined,
      bloco: bloco || undefined,
      busca: busca.trim() || undefined,
      pagina,
      pageSize,
    });
    setLoading(false);
    if (!res.sucesso) {
      setErro(res.mensagem || "Não foi possível carregar os lançamentos.");
      return;
    }
    setLinhas(res.linhas);
    setTotal(res.total);
  }, [id, competencia, bloco, busca, pagina, pageSize]);

  useEffect(() => {
    carregarLancamentos();
  }, [carregarLancamentos]);

  // Mesma correção aplicada em LancamentosEcd/LancamentosEcf: esta tela usa paginação
  // SERVER-SIDE, então `linhas` é só a página atual — exportar direto perderia o resto do
  // filtro silenciosamente sempre que total > pageSize. Busca todas as páginas (pageSize
  // máximo do backend, 100) respeitando o filtro atual antes de gerar o CSV.
  async function handleExportar() {
    setExportando(true);
    try {
      const nomeArquivo = `lancamentos_contribuicoes_${formatarCnpj(apuracao?.cnpj)}_${bloco}${competencia ? `_${competencia}` : ""}.csv`;
      let acumulado = [];
      let paginaBusca = 1;
      const PAGE_SIZE_EXPORT = 100;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const res = await buscarLancamentosContribuicoesApuracao(id, {
          competencia: competencia || undefined,
          bloco: bloco || undefined,
          busca: busca.trim() || undefined,
          pagina: paginaBusca,
          pageSize: PAGE_SIZE_EXPORT,
        });
        if (!res.sucesso || !res.linhas?.length) break;
        acumulado = acumulado.concat(res.linhas);
        if (acumulado.length >= res.total || res.linhas.length < PAGE_SIZE_EXPORT) break;
        paginaBusca += 1;
      }
      exportarCsv(acumulado, nomeArquivo);
    } finally {
      setExportando(false);
    }
  }

  const competenciaOpcoes = useMemo(() => {
    const base = competenciasDisponiveis.map((ym) => ({ value: ym, label: fmtMesAno(ym) }));
    const comTodas = [{ value: "", label: "Todas" }, ...base];
    if (competencia && !competenciasDisponiveis.includes(competencia)) {
      return [comTodas[0], { value: competencia, label: fmtMesAno(competencia) }, ...base];
    }
    return comTodas;
  }, [competenciasDisponiveis, competencia]);

  function handleFiltroCompetencia(novaCompetencia) {
    setCompetencia(novaCompetencia);
    setPagina(1);
    const params = { bloco };
    if (novaCompetencia) params.competencia = novaCompetencia;
    setSearchParams(params);
  }

  if (loading && linhas.length === 0 && !erro) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <GridLoader color="#1565c0" size={12} />
      </Box>
    );
  }

  const blocoLabel = BLOCO_OPCOES.find((o) => o.value === bloco)?.label || bloco || "—";

  return (
    // navigate(-1): `id` aqui é o id da apuração Contribuições (filha), não o do pai ICMS que
    // /detalhe/icms espera — mesmo padrão de LancamentosEcd/LancamentosEcf.
    <BreadcrumbApuracao
      itens={[
        { label: "Apurações", onClick: () => navigate("/relatorios/apuracoes") },
        { label: `Apuração ICMS — ${apuracao?.empresa || "—"}`, onClick: () => navigate(-1) },
        { label: "Lançamentos Contribuições" },
      ]}
    >
      {/* Mesmo tratamento dado à coluna "Tipo" ausente em Lançamentos E111: sinalizar a
          derivação que ainda não foi validada em vez de apresentá-la como fato. */}
      <Alert severity="info" sx={{ mb: 2 }}>
        As colunas seguem o leiaute da tela genérica de lançamentos SPED Contribuições
        (Descrição · Registro · Natureza · Valor) — o leiaute completo de cada registro está
        no arquivo SPED, não nesta tela. A "Natureza" vem do próprio arquivo em M200/M210/M600/M610
        (contribuição apurada, sempre débito) e em F100 (IND_OPER: entrada = crédito, saída =
        débito); em C170 ela é derivada do CST_PIS (50–67 = crédito, 01–09 = débito) e ainda
        não foi validada pelo Tributário.
      </Alert>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <ApuracaoCardShell
        hideContainer
        innerCardSx={{ border: "none", margin: 0 }}
        conteudoComScrollProprio
        Icone={ReceiptLongIcon}
        titulo="Lançamentos do SPED Contribuições (PIS/COFINS)"
        subtitulo={
          <>
            {apuracao?.empresa || "—"} · {formatarCnpj(apuracao?.cnpj)}
            <br />
            Conta/ref.: {blocoLabel}
            {competencia ? ` · Competência: ${fmtMesAno(competencia)}` : ""}
          </>
        }
        acaoHeader={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ color: "#666", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
              {total} lançamentos no filtro
            </Typography>
            <Button
              variant="contained"
              size="small"
              disabled={total === 0 || exportando}
              onClick={handleExportar}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#18316e", minHeight: 40, whiteSpace: "nowrap", px: 2, "&:hover": { bgcolor: "#152858" } }}
            >
              {exportando ? "Exportando…" : "Exportar CSV"}
            </Button>
          </Box>
        }
        filtros={
          <>
            <SelectFiltro
              label="Competência"
              value={competencia}
              onChange={handleFiltroCompetencia}
              options={competenciaOpcoes}
            />
            <BuscaFiltro
              label="Descrição"
              placeholder="Buscar na descrição"
              value={busca}
              onChange={(v) => {
                setBusca(v);
                setPagina(1);
              }}
            />
          </>
        }
        paginacao={
          !loading &&
          linhas.length > 0 && (
            <Pagination
              paginationKey="lancamentos-contribuicoes"
              serverSideTotal={total}
              serverSidePage={pagina}
              serverSideRowsPerPage={pageSize}
              onServerPageChange={(novaPagina) => setPagina(novaPagina)}
              onServerRowsPerPageChange={(novoPageSize) => {
                setPageSize(novoPageSize);
                setPagina(1);
              }}
              disabled={loading}
            />
          )
        }
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <GridLoader color="#1565c0" size={10} />
          </Box>
        ) : linhas.length === 0 && !erro ? (
          <p className={styles.emptyMsg}>Nenhum lançamento encontrado com os filtros informados.</p>
        ) : (
            <div style={{ margin: "8px 0 0", overflow: "auto" }}>
              <table className={tableStyles.accordion_table}>
                <thead>
                  <tr>
                    <th scope="col">Descrição</th>
                    <th scope="col">Registro</th>
                    <th scope="col">Natureza</th>
                    <th scope="col" style={{ textAlign: "right" }}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l, i) => (
                    <tr key={`${l.blocoRegistro}-${i}`}>
                      <td>{l.descricao || "—"}</td>
                      <td style={{ fontFamily: "ui-monospace, monospace" }}>{l.blocoRegistro || "—"}</td>
                      <td>
                        {l.natureza === "Débito" || l.natureza === "Crédito" ? (
                          <span
                            className={styles.badgeTipo}
                            style={
                              l.natureza === "Débito"
                                ? { background: "#e3ecf7", color: "#1e4a7a" }
                                : { background: "#fdecd8", color: "#ad4e00" }
                            }
                          >
                            {l.natureza}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={{ textAlign: "right", fontFamily: "ui-monospace, monospace" }}>
                        {l.valor == null ? "—" : formatarMoedaBRL(l.valor)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        )}
      </ApuracaoCardShell>
    </BreadcrumbApuracao>
  );
}
